// card-list.ts — the phone "card / list per row" fallback for the Bridge's
// wide data grids (owner ruling 2026-09-06: on a phone a wide grid renders as
// stacked key:value cards, one per row, not a horizontal-scroll table). This
// is the console (Bridge) side; the Helm's glance/gantt/dag carry the same
// ruling in helm-bridge.
//
// ONE renderer for every grid page — the releases list plus the three admin
// grids (Users, Audit, Configuration templates) — the grid-machinery.ts
// precedent: the page hands ROWS and a per-row spec, this module owns the DOM,
// the tap target and the empty state. The VISUAL grid/cards switch is CSS (the
// phone media query hides the grid frame and shows the card column); this
// module owns only the JS that BUILDS cards, and a page builds them only while
// the phone query matches, so the desktop grid stays byte-identical and pays
// nothing above the breakpoint.

import { emptyState } from 'pi-stack/client/feedback-nav.js';

/** The one breakpoint the console's card fallback and its CSS agree on. */
export const PHONE_MEDIA = '(max-width: 640px)';

export interface CardField {
  /** The transposed column header. */
  key: string;
  /** The cell's value. A string renders as text (empty renders the em-dash
   * placeholder — the grid's blank vocabulary); an element is placed verbatim
   * (a status pill, a link). */
  value: string | HTMLElement;
}

export interface CardSpec {
  /** The card headline — the primary column (a release name, a person). */
  title: string;
  /** The tap target. A non-null href makes the whole card an anchor to the
   * row's surface; null/absent renders a non-interactive card (a dev host with
   * no subdomain scheme resolves every release href to null). */
  href?: string | null;
  /** A status pill/badge shown on the title row, trailing the title. */
  badge?: HTMLElement | null;
  /** The transposed row: one labeled line per field. */
  fields: CardField[];
  /** data-* attribute values to stamp on the card (e.g. { rel: id }). */
  data?: Record<string, string> | undefined;
}

function fieldValueNode(value: string | HTMLElement): HTMLElement {
  const v = document.createElement('span');
  v.className = 'hc-cardkv-v';
  if (typeof value === 'string') v.textContent = value === '' ? '—' : value;
  else v.appendChild(value);
  return v;
}

function renderCard(spec: CardSpec): HTMLElement {
  const anchor = !!spec.href;
  const card = document.createElement(anchor ? 'a' : 'div');
  card.className = 'hc-card hc-datacard';
  if (anchor && card instanceof HTMLAnchorElement) card.href = spec.href as string;
  for (const [k, val] of Object.entries(spec.data ?? {})) card.setAttribute(`data-${k}`, val);

  const top = document.createElement('div');
  top.className = 'hc-card-top';
  const name = document.createElement('span');
  name.className = 'hc-card-name';
  name.textContent = spec.title;
  top.appendChild(name);
  if (spec.badge) {
    const badge = document.createElement('span');
    badge.className = 'hc-datacard-badge';
    badge.appendChild(spec.badge);
    top.appendChild(badge);
  }
  card.appendChild(top);

  const fields = document.createElement('dl');
  fields.className = 'hc-cardkv';
  for (const f of spec.fields) {
    const k = document.createElement('dt');
    k.className = 'hc-cardkv-k';
    k.textContent = f.key;
    fields.appendChild(k);
    const vWrap = document.createElement('dd');
    vWrap.className = 'hc-cardkv-vwrap';
    vWrap.appendChild(fieldValueNode(f.value));
    fields.appendChild(vWrap);
  }
  card.appendChild(fields);
  return card;
}

/** Paint `specs` into `container` as a card list, replacing its children (the
 * grid's setGridOption('rowData') idiom). No specs renders the empty note. */
export function renderCardList(
  container: HTMLElement,
  specs: CardSpec[],
  opts?: { empty?: string },
): void {
  container.replaceChildren();
  if (specs.length === 0) {
    // THE empty-state primitive (#1655 slice D): one treatment — sprite glyph,
    // display-role headline — the page's own empty copy as the title.
    container.insertAdjacentHTML('beforeend', emptyState({ glyph: 'layers', title: opts?.empty ?? 'Nothing to show.' }));
    return;
  }
  const frag = document.createDocumentFragment();
  for (const spec of specs) frag.appendChild(renderCard(spec));
  container.appendChild(frag);
}

// ---------------------------------------------------------------------
// The generic row->card projection for the admin grids: transpose the row
// into title + key:value fields straight from the page's own display
// vocabulary (the SAME displayValue the grid machinery lists, copies and
// filters over — the card can never disagree with the cell). The releases
// list curates its own fields (20 columns is too many for a card), so it
// builds specs directly rather than through this.
// ---------------------------------------------------------------------

export interface GridCardsConfig<T> {
  /** Column order — the page's COLUMN_IDS. */
  columnIds: readonly string[];
  /** headerName for a colId (the live defs; '' columns are skipped). */
  headerName: (colId: string) => string;
  /** The eye's display value for a cell ('' renders the em dash). */
  displayValue: (colId: string, row: T) => string;
  /** The colId whose display is the card title (auto-omitted from fields). */
  titleCol: string;
  /** Extra colIds to omit from the fields (e.g. the actions cell). */
  skipCols?: readonly string[];
  /** Optional tap target. */
  href?: (row: T) => string | null;
  /** Optional data-* stamps. */
  data?: (row: T) => Record<string, string>;
}

export function gridRowsToCards<T>(rows: T[], cfg: GridCardsConfig<T>): CardSpec[] {
  const skip = new Set<string>([cfg.titleCol, ...(cfg.skipCols ?? [])]);
  return rows.map((row) => ({
    title: cfg.displayValue(cfg.titleCol, row) || '—',
    href: cfg.href?.(row) ?? null,
    data: cfg.data?.(row),
    fields: cfg.columnIds
      .filter((c) => !skip.has(c) && cfg.headerName(c).trim() !== '')
      .map((c) => ({ key: cfg.headerName(c), value: cfg.displayValue(c, row) })),
  }));
}

/** Watch the phone breakpoint. `onChange(isPhone)` fires on every crossing;
 * `isPhone()` reads the current state; `teardown()` detaches the listener
 * (the page pushes it to its unmount set). Defensive about a headless host
 * whose matchMedia is a bare `{ matches }` stub with no event surface. */
export function watchPhone(onChange: (isPhone: boolean) => void): {
  isPhone: () => boolean;
  teardown: () => void;
} {
  const mm: typeof matchMedia | null =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia.bind(window)
      : typeof matchMedia === 'function'
        ? matchMedia
        : null;
  if (!mm) return { isPhone: () => false, teardown: () => {} };
  const mq = mm(PHONE_MEDIA);
  const handler = (): void => onChange(mq.matches);
  mq.addEventListener?.('change', handler);
  return {
    isPhone: () => mq.matches,
    teardown: () => mq.removeEventListener?.('change', handler),
  };
}
